import { useEffect, useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { HocuspocusProvider, WebSocketStatus } from '@hocuspocus/provider';
import * as Y from 'yjs';
import { useSession } from '@/hooks/useSession';
import type { Artifact } from '@/lib/api';
import { buildCollabUrl } from '@/lib/collab-url';
import { isCollaborativeEditorEditable } from '@/lib/collab-editor-state';
import {
  buildPresenceUser,
  materializePresenceUsers,
  type AwarenessPresenceState,
} from '@/lib/collab-presence';
import { plainTextToTiptapDoc } from '@/lib/plain-text-tiptap';
import { buildEditorExtensions } from '@/lib/tiptap-extensions';
import {
  CollaboratorPresence,
  type CollaboratorPresence as PresenceItem,
} from './CollaboratorPresence';
import { ConnectionStateBanner, type CollabConnectionState } from './ConnectionStateBanner';
import { CollaborativeEditorToolbar } from './CollaborativeEditorToolbar';

interface Props {
  artifact: Artifact;
  onProviderChange?: (provider: HocuspocusProvider | null) => void;
}

export function CollaborativeBodyEditor({ artifact, onProviderChange }: Props): React.ReactElement {
  const { user, isAdmin } = useSession();
  const isOwnerOrAdmin = isAdmin || (user !== null && user.id === artifact.ownerId);
  const canEdit =
    user !== null &&
    (artifact.visibility === 'team' || isOwnerOrAdmin) &&
    artifact.status !== 'archived' &&
    artifact.status !== 'superseded';

  // Y.Doc must be re-created when the artifact id changes, otherwise navigating
  // without unmounting binds the new provider to the previous artifact content.
  const [yDoc, setYDoc] = useState<Y.Doc>(() => new Y.Doc());
  const [providerInstance, setProviderInstance] = useState<HocuspocusProvider | null>(null);
  const [state, setState] = useState<CollabConnectionState>(canEdit ? 'connecting' : 'read-only');
  const [forceCloseReason, setForceCloseReason] = useState<string | undefined>(undefined);
  const [presence, setPresence] = useState<PresenceItem[]>([]);
  const [readOnlySeedJson, setReadOnlySeedJson] = useState<unknown>(null);
  const editorEditable = isCollaborativeEditorEditable(canEdit, state);

  useEffect(() => {
    setYDoc(new Y.Doc());
    setProviderInstance(null);
    setState(canEdit ? 'connecting' : 'read-only');
    setForceCloseReason(undefined);
    setPresence([]);
  }, [artifact.id, canEdit]);

  useEffect(() => {
    if (canEdit) return;
    const candidate = artifact.richTextJson;
    const isValidTiptapDoc =
      candidate !== null &&
      typeof candidate === 'object' &&
      (candidate as { type?: unknown }).type === 'doc';
    const seedJson = isValidTiptapDoc
      ? candidate
      : plainTextToTiptapDoc(artifact.plainTextCache ?? '');
    setReadOnlySeedJson(seedJson);
  }, [artifact.id, canEdit, artifact.richTextJson, artifact.plainTextCache]);

  useEffect(() => {
    if (!canEdit || user === null) return;
    const url = buildCollabUrl(artifact.id);
    const me = buildPresenceUser(user);
    let hasSynced = false;
    const provider = new HocuspocusProvider({
      url,
      name: artifact.id,
      document: yDoc,
      onStatus({ status }): void {
        setState(prev => {
          if (prev === 'forced-close') return prev;
          if (status === WebSocketStatus.Connected) return hasSynced ? 'connected' : 'connecting';
          if (status === WebSocketStatus.Connecting) return 'connecting';
          return 'reconnecting';
        });
      },
      onSynced({ state: synced }): void {
        if (!synced) return;
        hasSynced = true;
        setState(prev => (prev === 'forced-close' ? prev : 'connected'));
      },
      onClose({ event }): void {
        if (event.code === 4401) {
          setForceCloseReason(event.reason || 'access revoked');
          setState('forced-close');
          provider.destroy();
          setProviderInstance(null);
          onProviderChange?.(null);
        }
      },
      onAwarenessUpdate({ states }): void {
        setPresence(
          materializePresenceUsers({
            currentUser: me,
            states: states as AwarenessPresenceState[],
          })
        );
      },
    });
    provider.awareness?.setLocalStateField('user', {
      id: me.id,
      name: me.displayName,
      color: me.color,
    });
    setProviderInstance(provider);
    onProviderChange?.(provider);
    return (): void => {
      provider.destroy();
      setProviderInstance(null);
      onProviderChange?.(null);
    };
  }, [artifact.id, canEdit, user, yDoc, onProviderChange]);

  const editor = useEditor(
    {
      extensions: buildEditorExtensions({
        yDoc,
        provider: providerInstance,
        currentUser: user !== null && canEdit ? buildPresenceUser(user) : null,
      }),
      editable: editorEditable,
    },
    [yDoc, providerInstance]
  );

  useEffect(() => {
    if (canEdit) return;
    if (editor === null || readOnlySeedJson === null) return;
    try {
      editor.commands.setContent(readOnlySeedJson as object, false);
    } catch {
      editor.commands.setContent(
        plainTextToTiptapDoc(artifact.plainTextCache ?? '') as object,
        false
      );
    }
  }, [editor, readOnlySeedJson, canEdit, artifact.plainTextCache]);

  useEffect(() => {
    if (editor === null) return;
    editor.setEditable(editorEditable);
  }, [editor, editorEditable]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <CollaboratorPresence collaborators={presence} />
      </div>
      <ConnectionStateBanner state={state} reason={forceCloseReason} />
      <CollaborativeEditorToolbar editor={editor} readOnly={!editorEditable} />
      <EditorContent
        editor={editor}
        className="artifact-prose min-h-[16rem] max-w-none rounded-md border border-border bg-surface p-4 focus:outline-none"
        aria-label="Artifact body"
      />
    </div>
  );
}
